import type { Department, Step } from "../shared/types";

// Starting points for a new procedure. Titles are translation keys, turned
// into text when the template is picked (the saved procedure is plain text).

type TemplateStep = Omit<Step, "title" | "required"> & { title: string; required?: boolean };

export type ProcedureTemplate = {
  key: string;
  icon: string;
  department: Department;
  schedule_days: number[];
  schedule_time: string | null;
  steps: TemplateStep[];
};

const check = (title: string): TemplateStep => ({ title, kind: "check", min_value: null, max_value: null, unit: null });
const temp = (title: string, min: number, max: number): TemplateStep => ({
  title,
  kind: "temperature",
  min_value: min,
  max_value: max,
  unit: "°C",
});

export const PROCEDURE_TEMPLATES: ProcedureTemplate[] = [
  {
    key: "fridge_log",
    icon: "thermostat",
    department: "kitchen",
    schedule_days: [1, 2, 3, 4, 5, 6, 7],
    schedule_time: "10:00",
    steps: [
      temp("workday.tpl.fridge_log.walk_in", 0, 5),
      temp("workday.tpl.fridge_log.line_fridge", 0, 5),
      temp("workday.tpl.fridge_log.freezer", -25, -18),
      { title: "workday.tpl.fridge_log.seals", kind: "yes_no", min_value: null, max_value: null, unit: null },
      { title: "workday.tpl.fridge_log.comments", kind: "text", min_value: null, max_value: null, unit: null, required: false },
    ],
  },
  {
    key: "opening",
    icon: "wb_sunny",
    department: "front_of_house",
    schedule_days: [1, 2, 3, 4, 5, 6, 7],
    schedule_time: "11:00",
    steps: [
      check("workday.tpl.opening.lights"),
      check("workday.tpl.opening.tables"),
      check("workday.tpl.opening.toilets"),
      check("workday.tpl.opening.pos"),
      { title: "workday.tpl.opening.float", kind: "number", min_value: null, max_value: null, unit: null },
      check("workday.tpl.opening.specials"),
    ],
  },
  {
    key: "closing",
    icon: "dark_mode",
    department: "front_of_house",
    schedule_days: [1, 2, 3, 4, 5, 6, 7],
    schedule_time: "23:00",
    steps: [
      check("workday.tpl.closing.cash_up"),
      check("workday.tpl.closing.floors"),
      check("workday.tpl.closing.bins"),
      check("workday.tpl.closing.equipment_off"),
      check("workday.tpl.closing.doors"),
    ],
  },
  {
    key: "kitchen_clean",
    icon: "cleaning_services",
    department: "kitchen",
    schedule_days: [1, 2, 3, 4, 5, 6, 7],
    schedule_time: "22:30",
    steps: [
      check("workday.tpl.kitchen_clean.surfaces"),
      check("workday.tpl.kitchen_clean.grill"),
      check("workday.tpl.kitchen_clean.fryer"),
      check("workday.tpl.kitchen_clean.labels"),
      check("workday.tpl.kitchen_clean.floor"),
    ],
  },
];

/** A template as a new procedure's fields, with its texts translated. */
export function fromTemplate(tpl: ProcedureTemplate, t: (key: string) => string) {
  return {
    name: t(`workday.tpl.${tpl.key}.name`),
    department: tpl.department,
    schedule_days: tpl.schedule_days,
    schedule_time: tpl.schedule_time,
    steps: tpl.steps.map((s) => ({ ...s, title: t(s.title), required: s.required ?? true })),
  };
}
